import React, { Component, useEffect } from 'react';
import {
    Table,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    TablePagination,
    TableCellProps,
    TableBody,
    Collapse,
    FormGroup,
    Box,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { OverFlowOption, RowData } from '../../js/types';
import { NoResults } from './ReactError';
import { ReactIconBtn } from './ReactButton';
import { OptionListType } from '../../js/api';
import { PaginationActionsTS } from './ReactPaginationTS';
import { ThousandsDot } from '../../js/function';
import { useContext } from "react";
import { MainPageDialogContext } from "../../js/MainPageDialogContext";

type Column<T> = {
    name: string | JSX.Element;
    key: keyof T;
    btn?: JSX.Element;
    tbodyCellStyle?: React.CSSProperties;
} & TableCellProps;

/**
 * @param {"simple" | undefined} id 表格ID
 * @param {"simple" | undefined} aria <simple | undefined>
 * @param {boolean} hasRowsPerPage 是否可以切換每頁多少筆的查詢
 * @param {string} trClick
 * @param {string} searchFunction
 * @param {string} tableHead
 * @param {boolean} sticky 凍結表頭（縱向捲動時 header 不動），需同時設定 overflowY 與 maxHeight
 * @param {string} overflowX
 * @param {string} overflowY
 * @param {string} size
 * @param {string} maxHeight
 * @param {string} column
 * @param {string} data
 * @param {string} page
 * @param {string} rowsPerPage
 * @param {string} count
 * @param {string} className
 * @param {string} noResultText 查無結果的時候的文字
 * @param {number} frozenColumns 凍結前幾欄（從左側算起），需同時設定 overflowX="auto" 才能橫向捲動
 * @param {number} frozenRightColumns 凍結後幾欄（從右側算起）。不設定時會自動判斷：
 *     當表格寬度超出可視範圍，且最後一欄名稱為「操作」，就自動凍結最後一欄。
 *     傳 0 可關閉自動凍結。
 *
 * @example
 * // 凍結前 2 欄 + 凍結表頭（同時啟用）
 * // 角落 cell 的 z-index 會自動提升為 4，確保不被覆蓋
 * <ReactBasicTable2
 *     frozenColumns={2} // 凍結前 2 欄
 *     frozenRightColumns={1} // 凍結最後 1 欄（操作欄）
 *     overflowX={OverFlowOption.auto}// 必須，讓容器可橫向捲動
 * 
 * // 可同時搭配凍結表頭：
 *     sticky={true}
 *     overflowY={OverFlowOption.auto}
 *     maxHeight="500px"
 *     ...
 * />
 */
export type ReactBasicTable2Props<T extends Record<string, (React.ReactNode | string)>> = {
    trClick?: (elem: T, e?: Event) => Promise<void>
    searchFunction?: (page?: number, num_per_page?: number) => Promise<void>
    tableHead: Column<T>[];
    data: RowData<T>[];
    page?: number;
    rowsPerPage?: number;
    totalPage?: number;
    isCollaspe?: boolean;
    defaultCollapseOpen?: boolean
    id?: string | undefined
    aria?: "simple" | undefined
    hasRowsPerPage: boolean
    sticky?: boolean | undefined
    overflowX?: OverFlowOption | undefined
    overflowY?: OverFlowOption | undefined
    size?: "small" | "medium" | undefined
    maxHeight?: string | undefined
    column?: number | undefined
    className?: string | undefined
    hasHover?: boolean;
    hasBorder?: boolean;
    noResultText?: string;
    frozenColumns?: number;
    frozenRightColumns?: number;
}

type ReactBasicTable2State = {
    listCollapse: OptionListType[];
};

function ReactBasicTable2<T extends Record<string, (React.ReactNode | string)>>(props: ReactBasicTable2Props<T>) {
    const [listCollapse, setListCollapse] = React.useState<OptionListType[]>([]);
    const [data, setData] = React.useState<RowData<T>[]>([]);
    const { responseMessage } = useContext(MainPageDialogContext)

    const tableRef = React.useRef<HTMLTableElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [frozenOffsets, setFrozenOffsets] = React.useState<number[]>([]);
    const [frozenRightOffsets, setFrozenRightOffsets] = React.useState<number[]>([]);
    const [frozenHeaderBg, setFrozenHeaderBg] = React.useState<string>('');
    const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
    const [hoveredBg, setHoveredBg] = React.useState<string>('');
    const [isOverflowX, setIsOverflowX] = React.useState<boolean>(false);

    // 最後一欄是否為「操作」欄（欄位名稱為字串「操作」）
    const lastHead = props.tableHead ? props.tableHead[props.tableHead.length - 1] : undefined;
    const isLastColumnOperation = typeof lastHead?.name === 'string' && lastHead.name.trim() === '操作';

    // 未指定 frozenRightColumns 時，表格寬度超出可視範圍且最後一欄是「操作」就自動凍結最後一欄
    const frozenRightCount = props.frozenRightColumns ?? ((isOverflowX && isLastColumnOperation) ? 1 : 0);

    // 量測表格是否超出容器可視寬度（欄寬、視窗大小改變時重新量測）
    React.useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || props.frozenRightColumns !== undefined || !isLastColumnOperation) {
            setIsOverflowX(false);
            return;
        }
        // sticky 不影響版面寬度，所以凍結後再量測不會造成反覆切換
        const measure = () => setIsOverflowX(container.scrollWidth > container.clientWidth + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        if (tableRef.current) observer.observe(tableRef.current);
        return () => observer.disconnect();
    }, [data, props.tableHead, props.frozenRightColumns, isLastColumnOperation]);

    React.useLayoutEffect(() => {
        if ((!props.frozenColumns && !frozenRightCount) || !tableRef.current) {
            setFrozenOffsets([]);
            setFrozenRightOffsets([]);
            setFrozenHeaderBg('');
            return;
        }
        const ths = tableRef.current.querySelectorAll('thead tr th');
        const startIndex = props.isCollaspe ? 1 : 0;

        // 讀取 th 的實際計算背景色，供凍結欄 inline style 使用
        // （sticky 元素需要 inline background 才能正確覆蓋掄動的內容）
        const firstTh = ths[startIndex] as HTMLElement | undefined;
        if (firstTh) {
            setFrozenHeaderBg(window.getComputedStyle(firstTh).backgroundColor);
        }

        const offsets: number[] = [];
        let accumulated = 0;
        for (let i = 0; i < (props.frozenColumns ?? 0); i++) {
            offsets.push(accumulated);
            accumulated += ((ths[startIndex + i] as HTMLElement)?.offsetWidth ?? 0);
        }
        setFrozenOffsets(offsets);

        // 右側凍結欄：從最後一欄往前累加寬度，rightOffsets[i] 對應倒數第 i+1 欄
        const rightOffsets: number[] = [];
        let rightAccumulated = 0;
        for (let i = 0; i < frozenRightCount; i++) {
            rightOffsets.push(rightAccumulated);
            rightAccumulated += ((ths[ths.length - 1 - i] as HTMLElement)?.offsetWidth ?? 0);
        }
        setFrozenRightOffsets(rightOffsets);
    }, [data, props.frozenColumns, frozenRightCount, props.isCollaspe]);

    React.useEffect(() => {
        setData(props.data);
        setListCollapse(prev => props.data.map((item, index) => {
            if (prev.some(ele => ele.name === item.key)) {
                return prev.find(ele => ele.name === item.key)!
            } else return { name: item.key, value: (props.defaultCollapseOpen ? "Y" : "N") }
        }));
    }, [props.data]);

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        if (props.searchFunction) props.searchFunction(newPage);
    }

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (props.searchFunction) props.searchFunction(1, parseInt(e.target.value));
    }

    const handleTableRow = (elem: RowData<T>) => {
        // 處理展開收合
        if (listCollapse.length > 0) {
            let arr: ReactBasicTable2State["listCollapse"] = listCollapse;
            const filterArr = arr.filter(item => item.name === elem.key)
            if (filterArr.length > 0 && filterArr[0]) {
                if ('value' in filterArr[0]) {
                    if (filterArr[0].value === "Y") filterArr[0].value = "N";
                    else filterArr[0].value = "Y";
                } else console.error("Table Data Error!", filterArr);
            } else console.error("Table Data Error!", filterArr);
            //將新陣列設定進listCollapse
            setListCollapse(prev => prev.filter(item => item.name !== elem.key).concat(filterArr))
        } else {
            console.error("Table Data Error!", listCollapse);
        }

        if (props.trClick) {
            try { props.trClick(elem.data); }
            catch { console.error("ESD warning!", props.trClick); }
        }
    }

    if (data.length > 0) {
        // 當 frozenColumns 啟用時，<table> 不可設 overflow（否則會截斷 position:sticky 的作用範圍）
        // overflow 改由外層 TableContainer（div）統一管理
        const hasFrozen = !!props.frozenColumns || !!frozenRightCount;
        let style_obj: React.CSSProperties = {
            minWidth: 650,
            position: 'relative',
            borderCollapse: props.sticky ? 'collapse' : undefined,
            overflowX: hasFrozen ? undefined : (props.overflowX === undefined ? "auto" : props.overflowX),
            overflowY: hasFrozen ? undefined : (props.overflowY === undefined ? "hidden" : props.overflowY),
            maxHeight: props.overflowY === undefined ? "auto" :
                (props.maxHeight === undefined ? 475 : props.maxHeight),
        }
        let column = {
            minWidth: props.column === undefined ? undefined : 170,
        }
        const getFrozenStyle = (index: number, isHeader: boolean, rowKey?: string): React.CSSProperties => {
            const isLeft = !!props.frozenColumns && index < props.frozenColumns && frozenOffsets.length > 0;
            const rightIndex = props.tableHead.length - 1 - index; // 倒數第幾欄（0 = 最後一欄）
            const isRight = !!frozenRightCount && rightIndex < frozenRightCount && frozenRightOffsets.length > 0;
            if (!isLeft && !isRight) return {};
            const bodyBg = (!isHeader && rowKey && hoveredKey === rowKey && hoveredBg)
                ? hoveredBg
                : '#fff';
            return {
                position: 'sticky',
                ...(isLeft
                    ? { left: frozenOffsets[index] }
                    : {
                        right: frozenRightOffsets[rightIndex],
                        // 右側凍結區最左邊那欄加上陰影，區隔捲動中的內容
                        boxShadow: rightIndex === frozenRightCount - 1 ? '-2px 0 4px -2px rgba(0,0,0,0.2)' : undefined,
                    }),
                zIndex: isHeader ? (props.sticky ? 4 : 3) : 1,
                background: isHeader ? (frozenHeaderBg || undefined) : bodyBg,
            };
        };
        return (
            <React.Fragment>
                {props.hasRowsPerPage ?
                    <Box display="flex" justifyContent="flex-end" width="100%">
                        <TablePagination
                            rowsPerPageOptions={[
                                { value: 5, label: "5 筆" },
                                { value: 10, label: "10 筆" },
                                { value: 15, label: "15 筆" },
                                { value: 20, label: "20 筆" },
                                { value: 25, label: "25 筆" },
                            ]}
                            component="div"
                            labelRowsPerPage={"每頁行數"}
                            page={props.page ? (props.page - 1) : 0}
                            rowsPerPage={props.rowsPerPage ? props.rowsPerPage : 0}
                            count={-1}
                            onPageChange={handlePageChange}
                            nextIconButtonProps={{ style: { display: "none" } }}
                            backIconButtonProps={{ style: { display: "none" } }}
                            onRowsPerPageChange={handlePageSizeChange}
                        />
                    </Box> : null
                }
                <TableContainer
                    ref={containerRef}
                    id={props.id}
                    className={props.className === undefined ? "" : props.className}
                    style={{
                        boxShadow: "none",
                        maxHeight: style_obj.maxHeight,
                        overflowX: props.overflowX ?? 'auto',
                        overflowY: hasFrozen ? (props.overflowY ?? 'hidden') : undefined,
                    }}>
                    <Table ref={tableRef} stickyHeader={props.sticky === undefined ? false : props.sticky}
                        style={style_obj}
                        size={props.size}>
                        {props.tableHead !== undefined ?
                            <TableHead data-aria={props.aria}>
                                <TableRow>
                                    {props.isCollaspe ? <TableCell></TableCell> : null}
                                    {props.tableHead.map((item, index) => (
                                        <TableCell key={index}
                                            align={item.align}
                                            style={{ ...(item.style === undefined ? column : item.style), ...getFrozenStyle(index, true) }}
                                            width={item.width}
                                            className={props.className === undefined ? "" : props.className + ' ' + (item.className ? item.className : '')}
                                            colSpan={item.colSpan}>
                                            {item.name}
                                            {item.btn}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead> :
                            null}
                        <TableBody style={{ background: props.aria === "simple" ? "none" : "#fff" }}>
                            {data.map((elem: RowData<T>, index) => (
                                <React.Fragment key={elem.key}>
                                    <TableRow
                                        key={elem.key}
                                        className={elem.hasHover !== undefined ?
                                            elem.hasHover === false ?
                                                ("noHover " + (props.hasBorder === false ? "noBorder " : "") + elem.className) :
                                                elem.className :
                                            props.hasHover === false ?
                                                ("noHover " + (props.hasBorder === false ? "noBorder " : "") + elem.className) :
                                                elem.className}
                                        onClick={async (e: React.MouseEvent) => {
                                            const target = e.target as HTMLElement;
                                            // Ignore row click behavior when clicking interactive controls inside cells.
                                            if (target.closest('input, textarea, select, button, a, [role="button"], .MuiIconButton-root, .MuiInputBase-root, .MuiInputBase-input, .MuiSelect-root')) {
                                                return;
                                            }
                                            //避免在使用者拖曳選字時誤觸發列點擊事件
                                            const sel = (typeof window !== 'undefined' && window.getSelection) ? window.getSelection() : null;
                                            if (!sel || sel.toString().trim() === '') {
                                                handleTableRow(elem);
                                            } else {
                                                let clipboardText = await navigator.clipboard.readText();
                                                let selText = sel.toString().trim();
                                                if (selText && clipboardText && selText !== clipboardText) {
                                                    navigator.clipboard.writeText(sel.toString().trim());
                                                    responseMessage({ message: "已複製: " + sel.toString().trim(), success: true, data: null });
                                                    //sel.removeAllRanges(); //清除選取
                                                }
                                            }
                                        }}
                                        onSelect={(e: React.SyntheticEvent) => {
                                            const target = e.target as HTMLElement | null;
                                            // Let native selection behavior work inside form controls.
                                            if (target && target.closest('input, textarea, select, button, [contenteditable="true"], .MuiInputBase-root, .MuiInputBase-input')) {
                                                return;
                                            }
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => {
                                            if (!hasFrozen) return;
                                            if (props.hasHover === false || elem.hasHover === false) return;
                                            const bg = window.getComputedStyle(e.currentTarget).backgroundColor;
                                            setHoveredKey(elem.key);
                                            setHoveredBg(bg);
                                        }}
                                        onMouseLeave={() => {
                                            if (!hasFrozen) return;
                                            setHoveredKey(null);
                                            setHoveredBg('');
                                        }}
                                        {...elem.extroProps}>
                                        {props.isCollaspe ?
                                            elem.data.collapseComponent ?
                                                <TableCell>
                                                    <ReactIconBtn
                                                        color={"primary"}
                                                        size="small"
                                                        icon={listCollapse.length !== 0 && listCollapse.filter(item => item.name === elem.key) && listCollapse.filter(item => item.name === elem.key)[0].value === "Y" ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                        click={() => handleTableRow(elem)}
                                                    />
                                                </TableCell>
                                                :
                                                <TableCell></TableCell>
                                            :
                                            null
                                        }
                                        {props.tableHead.map((thItem, seq) => {
                                            if (thItem.name === "排序" && thItem.key === "seq")
                                                return <TableCell key={seq} align={thItem.align ? thItem.align : "left"} data-colname={thItem.name + ': '}
                                                    className={thItem.className ? thItem.className : ''}
                                                    style={getFrozenStyle(seq, false, elem.key)}
                                                >{index + 1}</TableCell>;
                                            else
                                                return <TableCell key={seq} align={thItem.align ? thItem.align : "left"} style={{ ...(thItem.tbodyCellStyle === undefined ? column : thItem.tbodyCellStyle), ...getFrozenStyle(seq, false, elem.key) }}
                                                    data-colname={thItem.name + ': '} className={thItem.className ? thItem.className : ''}
                                                >{elem.data[thItem.key] as React.ReactNode}</TableCell>;
                                        })}
                                    </TableRow>
                                    {elem.data.collapseComponent ?
                                        <TableRow className="noHover MuiTable-expanded">
                                            <TableCell
                                                colSpan={props.tableHead.length + 1}
                                                className="noBorderTop"
                                                style={{
                                                    paddingTop: 0, paddingBottom: 0,
                                                    background: '#f6f6f8' //"#f1f8ff" 
                                                }}>
                                                <Collapse
                                                    in={listCollapse.length !== 0 && listCollapse.filter(item => item.name === elem.key) && listCollapse.filter(item => item.name === elem.key)[0].value === "Y" ? true : false}
                                                    timeout="auto">
                                                    <FormGroup style={{ padding: "0 1rem" }}>
                                                        {elem.data.collapseComponent}
                                                    </FormGroup>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                        :
                                        null
                                    }
                                </React.Fragment>
                            ))}
                        </TableBody>

                    </Table>
                </TableContainer>
                {props.hasRowsPerPage ?
                    <PaginationActionsTS
                        totalPage={props.totalPage}
                        page={props.page}
                        onPageChange={handlePageChange}
                    /> : null}
            </React.Fragment >
        );
    } else return <NoResults title={props.noResultText} />;
}


/** 表格金額
 * @description 用來顯示金額(千分位跟靠右對齊)
 * @param amount 金額
 */
interface TableCellAmountProps {
    amount: number;
}
/** 表格金額 */
export const TableCellAmount = (props: TableCellAmountProps) => {
    const amount = props.amount === 0 ?
        "0" :
        props.amount ?
            ThousandsDot(props.amount) :
            "--";

    return <TableCell style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
        {amount}
    </TableCell>
}
/**
 * @author Daniel Wang
 * @description 據說memo讓效能變好，我也是看看而已啦
 */
// const ReactBasicTable2 = React.memo<T extends Record<string, (React.ReactNode | string)>>(ReactBasicTable1);
export { ReactBasicTable2 };